/**
 * Mode recognition — "After this drone, which mode did you hear?"
 *
 * The user picks from the seven church modes. The audio establishes a tonic
 * with a neutral drone (no 5th, so locrian doesn't clash) and then plays
 * the mode's ascending scale so the characteristic interval — lydian's ♯4,
 * mixolydian's ♭7, dorian's ♮6, phrygian's ♭2, locrian's ♭5 — is exposed.
 *
 * Distractors: every option is a mode name, so the option set is fixed at
 * the seven modes regardless of family. Practice within a node still drills
 * one target mode at a time; the option set just stays consistent across
 * nodes so the user is always picking from the full ear-training vocabulary.
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import {
  appendModalContext,
  pickKey,
  MODE_PATTERNS,
  MODE_DISPLAY_NAMES,
  type Mode,
  type KeyChoice,
} from "../utils.js";

const ALL_MODES: Mode[] = [
  "ionian",
  "dorian",
  "phrygian",
  "lydian",
  "mixolydian",
  "aeolian",
  "locrian",
];

/** Pretty label shown on the answer button (capitalized; "Major"/"Minor" for ionian/aeolian). */
function modeButtonLabel(m: Mode): string {
  const name = MODE_DISPLAY_NAMES[m];
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export interface ModeParams {
  mode: Mode;
}

export function generateMode(
  itemId: string,
  params: ModeParams,
  seed: number,
): Exercise {
  const rng = new Rng(seed);
  const key = pickKey(rng, params.mode);

  // Build audio: drone establishes tonic, then ascending scale in the mode.
  const builder = new AudioPlanBuilder();
  appendModalContext(builder, key);
  appendAscendingScale(builder, key, params.mode);
  const audio = builder.build();

  // Always present all seven modes as options. The button label uses
  // "Major"/"Minor" for ionian/aeolian to match how musicians talk.
  const options: AnswerOption[] = ALL_MODES.map((m) => ({
    id: m,
    label: modeButtonLabel(m),
    sublabel: m === "ionian" ? "ionian" : m === "aeolian" ? "aeolian" : undefined,
  }));

  return {
    itemId,
    exerciseType: "mode-recognition",
    audio,
    prompt: "Which mode did you hear?",
    options,
    correctAnswerId: params.mode,
    meta: {
      keyName: key.name,
      seed,
      explanation: `${modeButtonLabel(params.mode)} mode${
        params.mode === "ionian" || params.mode === "aeolian" ? ` (${params.mode})` : ""
      } in ${key.name}.`,
      distractorCount: ALL_MODES.length - 1,
      teachAudio: buildModeTeachPlan(key, params.mode),
    },
  };
}

/**
 * Append an ascending scale (one octave) starting at `tonicMidi + 12` so it
 * sits above the drone for clarity. Each note ~0.5s, briefly overlapping for
 * a connected line.
 */
function appendAscendingScale(
  builder: AudioPlanBuilder,
  key: KeyChoice,
  mode: Mode,
  opts: { noteDur?: number; noteGap?: number; gain?: number } = {},
): void {
  const noteDur = opts.noteDur ?? 0.5;
  const noteGap = opts.noteGap ?? 0.05;
  const gain = opts.gain ?? 0.22;
  const pattern = MODE_PATTERNS[mode];
  const base = key.tonicMidi + 12;
  for (const offset of pattern) {
    builder.addNote(base + offset, noteDur, gain, "melody");
    builder.advance(noteDur + noteGap);
  }
  // Land on the upper tonic so the ear hears the full octave.
  builder.addNote(base + 12, noteDur * 1.6, gain, "target");
  builder.advance(noteDur * 1.6 + 0.3);
}

/**
 * Wrong-answer teach plan: replay the scale slower with longer holds on the
 * characteristic degree, so the modal "color" lands. The characteristic
 * degree is the one that distinguishes the mode from major or natural minor:
 * lydian ♯4, mixolydian ♭7, dorian ♮6, phrygian ♭2, locrian ♭5. Ionian
 * and aeolian have no characteristic degree of their own (they ARE the
 * reference points), so we just hold tonic-3rd-tonic to expose the
 * major/minor third.
 */
function buildModeTeachPlan(key: KeyChoice, mode: Mode): AudioPlan {
  const builder = new AudioPlanBuilder();
  // Re-establish drone at modest length.
  appendModalContext(builder, key, { droneDuration: 1.6, postRoll: 0.3 });

  const pattern = MODE_PATTERNS[mode];
  const base = key.tonicMidi + 12;
  const characteristicIdx = characteristicDegreeIndex(mode);
  const noteDur = 0.7;
  const noteGap = 0.08;
  const gain = 0.22;
  for (let i = 0; i < pattern.length; i++) {
    const isChar = i === characteristicIdx;
    const dur = isChar ? noteDur * 1.8 : noteDur;
    const voice = isChar ? "target" : "melody";
    builder.addNote(base + pattern[i]!, dur, gain, voice);
    builder.advance(dur + noteGap);
  }
  builder.addNote(base + 12, noteDur * 1.4, gain, "melody");
  builder.advance(noteDur * 1.4 + 0.3);
  return builder.build();
}

/**
 * Index (0..6) of the scale degree that uniquely characterizes each mode.
 * `null` for ionian and aeolian — they're the reference modes, no single
 * note distinguishes them.
 */
function characteristicDegreeIndex(mode: Mode): number | null {
  switch (mode) {
    case "lydian":     return 3;  // ♯4
    case "mixolydian": return 6;  // ♭7
    case "dorian":     return 5;  // ♮6 (vs aeolian's ♭6)
    case "phrygian":   return 1;  // ♭2
    case "locrian":    return 4;  // ♭5
    case "ionian":
    case "aeolian":    return null;
  }
}
